/**
 * Public diagnostics & procedures reads (M8-T02 endpoints). M8-T03 uses
 * `fetchProcedures` (catalog) + `fetchProcedure` (facilities) for the search
 * screen; M8-T04 uses `fetchHospitalProcedures` for the profile section.
 * Read-only — booking a diagnostic is later-phase.
 */
import { apiFetch } from '../lib/api'
import type { SearchInterpretation } from './hospitals'

/** The closed 15 diagnostic categories (ADR-0016). Slugs match the backend. */
export const DX_CATEGORIES: ReadonlyArray<{ slug: string; name: string }> = [
  { slug: 'imaging-radiology', name: 'Imaging & Radiology' },
  { slug: 'laboratory', name: 'Laboratory' },
  { slug: 'cardiac', name: 'Cardiac' },
  { slug: 'neurology', name: 'Neurology' },
  { slug: 'endoscopy-gi', name: 'Endoscopy & Gastroenterology' },
  { slug: 'urology', name: 'Urology' },
  { slug: 'gynaecology', name: 'Gynaecology' },
  { slug: 'respiratory', name: 'Respiratory' },
  { slug: 'ophthalmology', name: 'Ophthalmology' },
  { slug: 'ent', name: 'ENT' },
  { slug: 'oncology-diagnostics', name: 'Oncology & Cancer Diagnostics' },
  { slug: 'surgical-diagnostics', name: 'General Surgical Diagnostics' },
  { slug: 'vascular', name: 'Vascular' },
  { slug: 'orthopaedic-msk', name: 'Orthopaedic & MSK' },
  { slug: 'executive-preventive', name: 'Executive & Preventive Health' },
]

/** A specialty that performs a procedure (M26-T05, ADR-0052). */
export type SpecialtyRef = {
  slug: string
  name: string
  is_primary: boolean
}

/**
 * A catalog entry with how many partner hospitals offer it.
 *
 * `category` IS NULL FOR A CLINICAL PROCEDURE and that is not a gap: ADR-0052
 * keeps the 15 categories for labs and scans and reaches everything else
 * through `specialties`. Render one or the other — never an empty label.
 */
export type ProcedureListItem = {
  id: string
  slug: string
  name: string
  category: string | null
  category_slug: string | null
  specialties: SpecialtyRef[]
  referral_required: boolean
  prep_notes: string | null
  /** Most-searched flag driving the popular-first entry (M12-T04). */
  popular: boolean
  /**
   * True when no single price exists even in principle — render "Cost
   * variable". DISTINCT from having no price yet, which reads "Not yet
   * available". Conflating them tells a patient an X-ray has no fixed price
   * when the truth is nobody has asked.
   */
  price_varies: boolean
  offer_count: number
}

/** A hospital offering a procedure. */
export type Facility = {
  hospital_slug: string
  hospital_name: string
  city: string
  price_pesewas: number | null
  /** True when `price_pesewas` is the least this hospital charges rather than
   *  the whole of it — renders "From GHS X" (ADR-0054). */
  price_is_minimum: boolean
  turnaround: string | null
  distance_km?: number
}

export type ProcedureDetail = {
  id: string
  slug: string
  name: string
  /**
   * OPTIONAL HERE, NULLABLE ON THE LIST — and the difference is real. This
   * route sets `response_model_exclude_none` so `distance_km` is absent rather
   * than null when no geo is supplied, and the same flag necessarily drops a
   * null `category`. Read it through `procedureHeading()`, which treats absent
   * and null alike.
   */
  category?: string | null
  category_slug?: string | null
  specialties: SpecialtyRef[]
  referral_required: boolean
  prep_notes: string | null
  price_varies: boolean
  facilities: Facility[]
}

/** A procedure as offered by one hospital (profile section, M8-T04). */
export type HospitalProcedureItem = {
  slug: string
  name: string
  referral_required: boolean
  price_varies: boolean
  price_pesewas: number | null
  price_is_minimum: boolean
  turnaround: string | null
}

/**
 * A hospital's procedures grouped under one heading.
 *
 * The heading is a CATEGORY for a lab or scan and a SPECIALTY for a clinical
 * procedure — two taxonomies in one list (ADR-0052). `kind` says which, so
 * they are never silently conflated; `category_slug` is null on a specialty
 * group.
 */
export type HospitalProceduresCategory = {
  heading: string
  kind: 'category' | 'specialty'
  category: string | null
  category_slug: string | null
  procedures: HospitalProcedureItem[]
}

/**
 * Where a procedure sits in the catalogue, as one label.
 *
 * A lab or scan has one of the 15 categories; a clinical procedure has none
 * and is placed by the discipline that performs it (ADR-0052). Rendering
 * `category` directly is a TYPE-SAFE WAY TO PRINT NOTHING — React renders null
 * as empty, so `{p.category} · 3 hospitals` silently becomes ` · 3 hospitals`
 * and the compiler is perfectly happy. Always go through this.
 */
export function procedureHeading(p: {
  category?: string | null
  specialties: SpecialtyRef[]
}): string {
  if (p.category) return p.category
  const primary = p.specialties.find((s) => s.is_primary) ?? p.specialties[0]
  return primary?.name ?? 'Other procedures'
}

/**
 * What the catalogue is being narrowed by. `category` reaches labs and scans,
 * `specialty` reaches clinical procedures — two taxonomies over one list
 * (ADR-0052) — and `q` matches a name or any alias, which is why it has to go
 * to the server rather than being filtered here.
 */
export type ProcedureFilters = {
  category?: string | null
  specialty?: string | null
  q?: string
}

export function proceduresKey(f: ProcedureFilters = {}) {
  return [
    'procedures',
    f.category ?? null,
    f.specialty ?? null,
    (f.q ?? '').trim(),
  ] as const
}

export function hospitalProceduresKey(slug: string) {
  return ['hospital-procedures', slug] as const
}

export function procedureKey(slug: string) {
  return ['procedure', slug] as const
}

/** GET /api/v1/procedures?category=&q= — catalog search. */
/**
 * The catalogue, plus what the search understood.
 *
 * `interpretation` IS RETURNED, NOT DISCARDED, and that is deliberate: the
 * specialist client used to drop exactly this field, so the server disclosed an
 * emergency phrase and the page rendered "No specialists found" instead
 * (M22-T12). It is null whenever the literal search answered — its presence is
 * itself the signal that we inferred.
 */
export type ProcedureSearchResult = {
  data: ProcedureListItem[]
  interpretation: SearchInterpretation | null
}

export async function fetchProcedures(
  f: ProcedureFilters = {},
  signal?: AbortSignal,
): Promise<ProcedureSearchResult> {
  const params = new URLSearchParams()
  if (f.category) params.append('category', f.category)
  if (f.specialty) params.append('specialty', f.specialty)
  if (f.q?.trim()) params.append('q', f.q.trim())
  const qs = params.toString()
  const opts = signal ? { signal } : {}
  const body = await apiFetch<{
    data: ProcedureListItem[]
    interpretation?: SearchInterpretation | null
  }>(`/api/v1/procedures${qs ? `?${qs}` : ''}`, opts)
  return { data: body.data, interpretation: body.interpretation ?? null }
}

/** GET /api/v1/procedures/{slug} — a procedure + the facilities offering it. */
export async function fetchProcedure(
  slug: string,
  signal?: AbortSignal,
): Promise<ProcedureDetail> {
  const opts = signal ? { signal } : {}
  const body = await apiFetch<{ data: ProcedureDetail }>(
    `/api/v1/procedures/${slug}`,
    opts,
  )
  return body.data
}

/** GET /api/v1/hospitals/{slug}/procedures — a hospital's offerings by category. */
export async function fetchHospitalProcedures(
  slug: string,
  signal?: AbortSignal,
): Promise<HospitalProceduresCategory[]> {
  const opts = signal ? { signal } : {}
  const body = await apiFetch<{ data: HospitalProceduresCategory[] }>(
    `/api/v1/hospitals/${slug}/procedures`,
    opts,
  )
  return body.data
}

/** The "my procedure is not listed" lead (M12-T04, ADR-0015). */
export type ProcedureRequestInput = { description: string; area?: string }
export type ProcedureRequestCreated = { reference: string; status: string }

/** POST /api/v1/patient/procedure-requests — capture a free-text diagnostics
 * lead for the AMTC concierge. Needs a verified patient session `token`. */
export async function submitProcedureRequest(
  token: string,
  input: ProcedureRequestInput,
): Promise<ProcedureRequestCreated> {
  const body = await apiFetch<{ data: ProcedureRequestCreated }>(
    '/api/v1/patient/procedure-requests',
    {
      method: 'POST',
      token,
      body: {
        description: input.description,
        ...(input.area ? { area: input.area } : {}),
      },
    },
  )
  return body.data
}
